import {
  SpecialtyKeywordConfigType,
  SpecialtyKeywordRow
} from './SpecialtyKeywordTable';
import { Controller, get, useFormContext } from 'react-hook-form';
import { ButtonIcon, Checkbox, Table } from '@morressier/alembic';
import { AutosuggestSelect } from 'src/components/FormField/AutosuggestSelect';
import { Box, Tooltip } from '@chakra-ui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrashCan } from '@fortawesome/pro-regular-svg-icons';
import {
  fetchReviewerExpertiseSuggestionKeywords,
  fetchReviewerExpertiseSuggestionSpecialty
} from 'src/api/signUpMutation';
import { autosuggestSourceSearchMethod } from 'src/api/autosuggest';
import {
  FormFieldSelect as IFormFieldSelect,
  IAutosuggestConfig
} from '@publishing-engine/domain/types/article';

export const getConfigProperties = (config: IFormFieldSelect) => {
  const {
    name,
    caseDataFields: { main: field },
    params: { validations, labels }
  } = config;

  const autosuggest = (config.params as { autosuggest?: IAutosuggestConfig })
    .autosuggest;

  return {
    name,
    field,
    validations,
    labels,
    autosuggest
  };
};

const formatLabelText = (value?: string) =>
  (value ?? '')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim();

const getSearchMethod = (autosuggest?: IAutosuggestConfig) =>
  autosuggest && autosuggest.source !== 'freeText'
    ? autosuggestSourceSearchMethod[autosuggest.source]
    : undefined;

const SpecialtyKeywordTableRow = ({
  rowField,
  fieldName,
  index,
  handleAddRow,
  selected,
  toggleRow,
  handleDeleteRow,
  handleNewKeywordClick,
  handleCellChange,
  isKeywordOptionDisabled,
  configs,
  isMaxRowLimit
}: {
  rowField: any;
  fieldName: string;
  index: number;
  handleAddRow: any;
  toggleRow: any;
  selected: any;
  handleDeleteRow: any;
  handleNewKeywordClick: any;
  handleCellChange: any;
  isKeywordOptionDisabled: (rowIndex: number, keywordName?: string) => boolean;
  configs: SpecialtyKeywordConfigType;
  isMaxRowLimit: boolean;
}) => {
  const specialtyConfig = getConfigProperties(configs.Specialty);
  const subSpecialtyConfig = configs.SubSpecialty
    ? getConfigProperties(configs.SubSpecialty)
    : null;

  const keywordConfig = getConfigProperties(configs.Keyword);
  const scandinavianKeywordConfig = configs.ScandinavianKeyword
    ? getConfigProperties(configs.ScandinavianKeyword)
    : null;

  const {
    watch,
    formState: { errors },
    control
  } = useFormContext();

  const specialtyPath = `${fieldName}.${index}.specialty`;
  const subSpecialtyPath = `${fieldName}.${index}.subSpecialty`;
  const keywordPath = `${fieldName}.${index}.keyword`;
  const scandinavianKeywordPath = `${fieldName}.${index}.scandinavianKeyword`;

  const specialty = watch(specialtyPath) as SpecialtyKeywordRow['specialty'];
  const subSpecialty = watch(
    subSpecialtyPath
  ) as SpecialtyKeywordRow['subSpecialty'];
  const keyword = watch(keywordPath) as SpecialtyKeywordRow['keyword'];
  const scandinavianKeyword = watch(
    scandinavianKeywordPath
  ) as SpecialtyKeywordRow['scandinavianKeyword'];

  const specialtyError = get(errors, specialtyPath);
  const subSpecialtyError = get(errors, subSpecialtyPath);
  const keywordError = get(errors, keywordPath);
  const scandinavianKeywordError = get(errors, scandinavianKeywordPath);

  // Resolve the first non-empty `termId` among the configured parent fields,
  // substituting the current row index into the `{index}` token. Returns
  // undefined when no parent is configured (root autosuggest).
  const getParentTermId = (autosuggest?: IAutosuggestConfig) => {
    if (!autosuggest?.parentField) return undefined;
    const candidates = Array.isArray(autosuggest.parentField)
      ? autosuggest.parentField
      : [autosuggest.parentField];
    const termIdPath = autosuggest.termIdPath ?? 'termId';

    for (const path of candidates) {
      const parentValue = watch(path.replaceAll('{index}', String(index)));
      const termId = get(parentValue, termIdPath);
      if (termId !== undefined && termId !== null && termId !== '') {
        return String(termId);
      }
    }
    return undefined;
  };

  // Config-driven termSearch/isDisabled when `params.autosuggest` is present;
  // otherwise fall back to the historical hardcoded cascade so realms seeded
  // before this config existed keep working unchanged.
  const resolveDependent = (
    autosuggest: IAutosuggestConfig | undefined,
    fallbackTermSearch: string | undefined,
    fallbackDisabled: boolean
  ) => {
    if (!autosuggest) {
      return { termSearch: fallbackTermSearch, isDisabled: fallbackDisabled };
    }

    const termSearch = getParentTermId(autosuggest);
    const isDisabled =
      autosuggest.source === 'byTermId' ? !termSearch : false;
    return { termSearch, isDisabled };
  };

  const specialtyAuto = resolveDependent(
    specialtyConfig.autosuggest,
    undefined,
    false
  );
  const subSpecialtyAuto = resolveDependent(
    subSpecialtyConfig?.autosuggest,
    specialty?.termId,
    !specialty?.termId || false
  );
  const keywordAuto = resolveDependent(
    keywordConfig.autosuggest,
    subSpecialtyConfig ? subSpecialty?.termId : specialty?.termId,
    !specialty?.termId || false
  );
  const scandinavianKeywordAuto = resolveDependent(
    scandinavianKeywordConfig?.autosuggest,
    keyword?.termId,
    !specialty?.termId || false
  );

  return (
    <Table.Row
      key={rowField.id}
      alignItems="start"
      _last={{ borderBottom: 'none' }}
      data-cy="table-row"
      display="flex"
      gap={4}
      minWidth="100%"
      width="100%"
    >
      <Table.RowCell>
        <Checkbox
          ariaLabel={`Select row ${index + 1}`}
          id={`row-${index}`}
          name={`row-${index}`}
          isChecked={selected[index] || false}
          onChange={() => toggleRow(index)}
        >
          <Box as="span" srOnly>
            Select row {index + 1}
          </Box>
        </Checkbox>
      </Table.RowCell>

      <Table.RowCell flex={1}>
        <Controller
          control={control}
          name={specialtyPath}
          rules={{
            required: `${specialtyConfig.name} is required`
          }}
          render={() => (
            <AutosuggestSelect
              value={specialty || ''}
              onChange={value => handleCellChange(specialtyPath, value)}
              placeholder="specialty"
              isInvalid={!!specialtyError}
              searchMethod={
                getSearchMethod(specialtyConfig.autosuggest) ??
                fetchReviewerExpertiseSuggestionSpecialty
              }
              queryKey={specialtyPath}
              termSearch={specialtyAuto.termSearch}
              isDisabled={specialtyAuto.isDisabled}
              level={specialtyConfig.autosuggest?.level}
              errorMessage={specialtyError?.message as string}
              allowNewKeyword={specialtyConfig.autosuggest?.allowNew ?? false}
              onNewKeywordClick={() =>
                handleNewKeywordClick(index, 'specialty', 'specialty')
              }
              newKeywordLabel="specialty"
            />
          )}
        />
      </Table.RowCell>

      {subSpecialtyConfig && (
        <Table.RowCell flex={1}>
          <Controller
            control={control}
            name={subSpecialtyPath}
            rules={{
              required: subSpecialtyConfig.validations?.required
                ? `${subSpecialtyConfig.name} is required`
                : false
            }}
            render={() => (
              <AutosuggestSelect
                value={subSpecialty || ''}
                onChange={value => handleCellChange(subSpecialtyPath, value)}
                placeholder="sub-specialty"
                isInvalid={!!subSpecialtyError}
                errorMessage={subSpecialtyError?.message as string}
                searchMethod={
                  getSearchMethod(subSpecialtyConfig.autosuggest) ??
                  fetchReviewerExpertiseSuggestionKeywords
                }
                queryKey={subSpecialtyPath}
                termSearch={subSpecialtyAuto.termSearch}
                isDisabled={subSpecialtyAuto.isDisabled}
                level={subSpecialtyConfig?.autosuggest?.level}
                allowNewKeyword={
                  subSpecialtyConfig?.autosuggest?.allowNew ?? false
                }
                onNewKeywordClick={() =>
                  handleNewKeywordClick(index, 'subSpecialty', 'sub-specialty')
                }
                newKeywordLabel="sub-specialty"
              />
            )}
          />
        </Table.RowCell>
      )}

      <Table.RowCell flex={1}>
        <Controller
          control={control}
          name={keywordPath}
          rules={{
            required: keywordConfig.validations?.required
              ? `${keywordConfig.name} is required`
              : false
          }}
          render={() => (
            <AutosuggestSelect
              value={keyword || ''}
              onChange={value => handleCellChange(keywordPath, value)}
              placeholder="keyword"
              isInvalid={!!keywordError}
              errorMessage={keywordError?.message as string}
              searchMethod={
                getSearchMethod(keywordConfig.autosuggest) ??
                fetchReviewerExpertiseSuggestionKeywords
              }
              queryKey={keywordPath}
              termSearch={keywordAuto.termSearch}
              isDisabled={keywordAuto.isDisabled}
              level={keywordConfig.autosuggest?.level}
              onNewKeywordClick={() =>
                handleNewKeywordClick(index, 'keyword', 'keyword')
              }
              newKeywordLabel="keyword"
              isOptionDisabled={(item: { name?: string }) =>
                isKeywordOptionDisabled(index, item?.name)
              }
            />
          )}
        />
      </Table.RowCell>

      {scandinavianKeywordConfig && (
        <Table.RowCell flex={1}>
          <Controller
            control={control}
            name={scandinavianKeywordPath}
            rules={{
              required: scandinavianKeywordConfig.validations?.required
                ? `${
                    formatLabelText(scandinavianKeywordConfig.name) ||
                    'Scandinavian Keyword'
                  } is required`
                : false
            }}
            render={() => (
              <AutosuggestSelect
                value={scandinavianKeyword || ''}
                onChange={value =>
                  handleCellChange(scandinavianKeywordPath, value)
                }
                placeholder="scandinavian keyword"
                isInvalid={!!scandinavianKeywordError}
                errorMessage={scandinavianKeywordError?.message as string}
                searchMethod={
                  getSearchMethod(scandinavianKeywordConfig.autosuggest) ??
                  fetchReviewerExpertiseSuggestionKeywords
                }
                queryKey={scandinavianKeywordPath}
                termSearch={scandinavianKeywordAuto.termSearch}
                isDisabled={scandinavianKeywordAuto.isDisabled}
                level={scandinavianKeywordConfig?.autosuggest?.level}
                onNewKeywordClick={() =>
                  handleNewKeywordClick(
                    index,
                    'scandinavianKeyword',
                    'scandinavian keyword'
                  )
                }
                newKeywordLabel="scandinavian keyword"
              />
            )}
          />
        </Table.RowCell>
      )}

      <Table.RowCell width={5}>
        <Tooltip label="Add row" placement="top" hasArrow>
          <Box>
            <ButtonIcon
              ariaLabel="Add row"
              icon={<FontAwesomeIcon icon={faPlus} />}
              data-cy="plus-icon"
              ml={1}
              onClick={handleAddRow}
              disabled={isMaxRowLimit}
            />
          </Box>
        </Tooltip>
      </Table.RowCell>

      <Table.RowCell width={10}>
        <Tooltip label="Delete row" placement="top" hasArrow>
          <Box>
            <ButtonIcon
              ariaLabel="Delete this row"
              icon={<FontAwesomeIcon icon={faTrashCan} />}
              data-cy={`delete-row-${index}`}
              ml={1}
              onClick={() => handleDeleteRow(index)}
            />
          </Box>
        </Tooltip>
      </Table.RowCell>
    </Table.Row>
  );
};

export default SpecialtyKeywordTableRow;
